<template>
  <Transition name="drawer-slide">
    <div v-if="isPinpointingLocation" class="add-location-drawer">
      <div ref="addLocationDrawerRef" class="drawer-card">
        <div class="drawer-card-title">{{ localize('locations.map.proposal.title.add_spot') }}</div>
        <div class="drawer-card-body">
          <GamSimpleLocationSearch v-bind="getPinpointSearchInput" class="drawer-search" />
          <GamInput
            v-if="gamForm"
            v-model="gamForm.form.name"
            :name="GamInputName.GAM_NAME"
            :placeholder="'locations.add.form.name.placeholder'"
            :left-icon="GamIconName.EDIT"
          />
        </div>
        <div class="drawer-card-ctas">
          <div class="cta-circle" @click="closeAddLocationFull">
            <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
          </div>
          <div class="cta-circle" @click="handleContinueFromPinpoint">
            <GamIcon :name="GamIconName.ARROW_RIGHT" :size="GamIconSize.LARGE" is-current />
          </div>
          <div v-if="canQuickSubmit || isLoading" class="cta-circle cta-check is-active" @click="handleQuickSubmit">
            <GamIcon v-if="!isLoading" :name="GamIconName.CHECK" :size="GamIconSize.X_LARGE" is-current />
            <div v-else class="cta-spinner" />
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <gam-side-panel v-bind="getAddLocSidePanel">
    <LocationForm ref="locationForm" :key="`add-location-${instanceId}`" :form-id="formId" :instance-id="instanceId" />
  </gam-side-panel>

  <div v-if="isPanelOpen" :class="['add-location-bottom-ctas', { 'is-desktop': !isMobile }]">
    <div class="cta-circle" @click="isMobile ? handleBackToPinpoint() : clearAddMode()">
      <GamIcon
        :name="isMobile ? GamIconName.ARROW_LEFT : GamIconName.X_CLOSE"
        :size="isMobile ? GamIconSize.LARGE : GamIconSize.X_LARGE"
        is-current
      />
    </div>

    <div v-if="!isInvalid || isLoading" class="cta-circle cta-check is-active" @click="handleSubmitLocation">
      <GamIcon v-if="!isLoading" :name="GamIconName.CHECK" :size="GamIconSize.X_LARGE" is-current />
      <div v-else class="cta-spinner" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MapLocationDto } from '@/core/data/location/location.interface';
import { LocationType, MapLocationSource } from '@/core/data/location/location.type';
import { gambits, localize } from '@/core/gambits';
import LocationHelper from '@/core/helpers/location.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useFormStore } from '@/stores/form.store';
import { useMapStore } from '@/stores/map.store';
import { useMarkerStore } from '@/stores/marker.store';
import { useSparkStore } from '@/stores/spark.store';
import { useTableStore } from '@/stores/table.store';
import GamIcon from '@/views/components/GamIcon.vue';
import GamInput from '@/views/components/GamInput.vue';
import GamSimpleLocationSearch from '@/views/components/GamSimpleLocationSearch.vue';
import GamSidePanel from '@/views/components/side-panel/GamSidePanel.vue';
import { GamButtonMessageVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import {
  GamCollapseDirection,
  GamSidePanelId,
  GamSidePanelOption,
} from '@/views/composables/constants/components/gamSidePanel.constants';
import { FormId } from '@/views/composables/constants/form.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { useDrawerHeight } from '@/views/composables/hooks/useDrawerHeight';
import type { GamButtonMessageType } from '@/views/composables/models/components/GamButton';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GamSimpleInputSearchType } from '@/views/composables/models/components/GamInput';
import type { GamSidePanelType } from '@/views/composables/models/components/GamSidePanel';
import type { LocationFormProps, LocationValidator } from '@/views/composables/models/form.interface';
import { storeToRefs } from 'pinia';
import { v4 as uuid4 } from 'uuid';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import LocationForm from '../forms/LocationForm.vue';
import { isLocationValid } from '../forms/validateLocation';
import { handleCreateLocation } from './locationActions';

const emits = defineEmits([GamComponentsEmits.SUCCESS]);

const listId = GamListId.ADD_LOCATION;
const panelId = GamSidePanelId.LOCATION_FORM;

const formId = FormId.ADD_LOCATION;
const formStore = useFormStore<LocationFormProps, LocationValidator>(formId)();
const { gamForm } = storeToRefs(formStore);

const tableStore = useTableStore<GamDropdownItem>(listId)();
const mapStore = useMapStore();
const sparkStore = useSparkStore();
const mapPinTableStore = useMarkerStore(GamListId.MARKERS)();

const addLocationDrawerRef = ref<HTMLElement | null>(null);
useDrawerHeight(addLocationDrawerRef);

const isLoading = ref<boolean>(false);
const errorMessage = ref<GamButtonMessageType | null>(null);
const locationForm = ref<InstanceType<typeof LocationForm>>();

const router = useRouter();
const route = useRoute();

const instanceId = ref<string>('');

const isPinpointingLocation = computed(
  () => route.query.mobile_step === 'pick_coordinate' && route.query.side_panel === 'add_location',
);

const { currentLocation } = storeToRefs(mapStore);

const handleClearPinpointLocation = () => {
  if (!gamForm.value) return;
  gamForm.value.form.name = '';
  gamForm.value.form.address = '';
  gamForm.value.form.coordinates = undefined;
  gamForm.value.form.pinpoint = undefined;
};

const getPinpointSearchInput = computed((): GamSimpleInputSearchType<GamDropdownItem> => {
  return {
    id: GamListId.LOCATION_FORM,
    onClickClear: handleClearPinpointLocation,
    input: {
      canClear: true,
      name: GamInputName.LOCATION,
      placeholder: 'locations.add.form.search.choose',
      leftIcon: GamIconName.LOCATION_EMPTY,
      value: gamForm.value?.form.address,
    },
    dropdown: {
      id: GamListId.LOCATION_FORM,
      reversed: true,
    },
    loadCallback: async (query: string) => {
      const result = await gambits.locationService.searchLocationsByQuery(query, {
        geo: currentLocation.value,
        mapbox: true,
      });

      return {
        ...result,
        data: LocationHelper.mapSearchLocations(result.data),
      };
    },
    selectCallback: (locationData: GamDropdownItem | null) => {
      if (!locationData || !gamForm.value) return;

      if (locationData.source === MapLocationSource.EXTERNAL || locationData.source === MapLocationSource.GOOGLE) {
        gamForm.value.form.name = locationData.label;

        if (locationData.id.indexOf('poi') > -1) {
          gamForm.value.form.address = `${locationData.subLabel}`;
        } else {
          gamForm.value.form.address = `${locationData.label}, ${locationData.subLabel}`;
        }

        const coordinates = {
          lat: parseFloat(String(locationData.coordinates.lat)),
          lng: parseFloat(String(locationData.coordinates.lng)),
        };

        if (
          typeof coordinates.lat !== 'number' ||
          Number.isNaN(coordinates.lat) ||
          typeof coordinates.lng !== 'number' ||
          Number.isNaN(coordinates.lng)
        ) {
          return;
        }

        gamForm.value.form.coordinates = coordinates;
        gamForm.value.form.pinpoint = coordinates;

        // @ts-ignore
        window.gMap?.map?.panTo(coordinates);
      }
    },
  };
});

const handleContinueFromPinpoint = () => {
  router.push({ ...route, query: { ...route.query, mobile_step: undefined } });
};

const handleBackToPinpoint = () => {
  router.push({ ...route, query: { ...route.query, mobile_step: 'pick_coordinate' } });
};

const isPanelOpen = computed(
  () => route.query.side_panel === 'add_location' && route.query.mobile_step !== 'pick_coordinate',
);

const canQuickSubmit = computed(() => {
  if (!gamForm.value?.form) return false;
  const { name, address, coordinates } = gamForm.value.form;
  return !!(name && address && coordinates?.lat && coordinates?.lng);
});

const isInvalid = ref<boolean>(true);

watch(
  gamForm,
  (gamForm) => {
    isInvalid.value = !isLocationValid(gamForm);
  },
  { deep: true },
);

const closeAddLocationFull = () => {
  mapStore.clearAddMode();

  router.push({
    ...route,
    query: {
      ...route.query,
      side_panel: undefined,
      mobile_step: undefined,
    },
  });
};

const clearAddMode = () => {
  mapStore.clearAddMode();

  if (isMobile.value) {
    history.back();

    return;
  }

  router.push({
    ...route,
    query: {
      ...route.query,
      side_panel: undefined,
      mobile_step: undefined,
    },
  });
};

// TODO: Refactor this once we don't have complex form generators in parent views
const clearForm = () => {
  errorMessage.value = null;

  const { form } = formStore.gamForm || {};
  if (!form) return;

  form.type = undefined;
  form.address = '';
  form.about = '';
  form.name = '';
  form.chessFeatures = [];
  form.generalFeatures = [];
  form.chessSetCount = undefined;
  form.chessClockCount = undefined;
  form.chessTableCount = undefined;
  form.giantChessBoardCount = undefined;
  form.giantChessPieces = false;
  form.photos = [];
  form.coordinates = undefined;
  form.pinpoint = undefined;
  form.openingHours = null;
};

clearForm();

onMounted(() => {
  nextTick(() => prefillFromMapSearch());
});

watch(
  () => route.query,
  (newPanel, prevPanel) => {
    if (prevPanel.side_panel !== 'add_location' && newPanel.side_panel === 'add_location') {
      clearForm();
      nextTick(() => prefillFromMapSearch());
    }
  },
);

const closeAddLocation = (locationId: string, lat?: number, lng?: number) => {
  tableStore.resetList(true);
  mapStore.clearAddMode();

  const { return_path, return_key, return_value } = route.query;

  if (return_path) {
    router.push({
      path: String(return_path),
      query: {
        [String(return_key)]: return_value,
        new_club_location: locationId,
      },
    });
  } else {
    router.push({
      ...route,
      query: {
        ...route.query,
        side_panel: undefined,
        spot: locationId,
      },
    });

    if (lat && lng) {
      // @ts-ignore
      const currentZoom = window.gMap?.map?.getZoom() ?? 0;
      if (currentZoom < 15) {
        // @ts-ignore
        window.gMap?.map?.setZoom(15);
      }
      // @ts-ignore
      window.gMap?.map?.panTo({ lat, lng });
    }
  }
};

const prefillFromMapSearch = () => {
  const loc = mapStore.newLocation;
  if (!loc || !gamForm.value) return;

  const coords = loc.coordinates;
  if (
    typeof coords?.lat !== 'number' ||
    Number.isNaN(coords.lat) ||
    typeof coords?.lng !== 'number' ||
    Number.isNaN(coords.lng)
  ) {
    return;
  }

  gamForm.value.form.name = loc.name || loc.label || '';
  gamForm.value.form.address = loc.address || loc.subLabel || '';
  gamForm.value.form.coordinates = coords;
  gamForm.value.form.pinpoint = coords;

  mapStore.setNewLocation(undefined);
};

onBeforeRouteUpdate((to, from) => {
  if (to.query.side_panel === 'add_location' && from.query.side_panel !== 'add_location') {
    clearForm();
    instanceId.value = uuid4();
    isInvalid.value = true;
    nextTick(() => prefillFromMapSearch());
  }
});

const handleSubmitLocation = async () => {
  if (!gamForm.value?.form || isInvalid.value || isLoading.value) return;

  isLoading.value = true;

  const result = await handleCreateLocation(gamForm.value.form);

  if (!result) {
    errorMessage.value = {
      label: 'Something went wrong.',
      variant: GamButtonMessageVariant.ERROR,
    };
    isLoading.value = false;
    return;
  }

  if ((result as GamButtonMessageType).variant === GamButtonMessageVariant.ERROR) {
    errorMessage.value = result as GamButtonMessageType;
    isLoading.value = false;
    return;
  }

  const { latitude, longitude } = mapPinTableStore.getLocationParam() || {};
  latitude && longitude && mapPinTableStore.addUniqueItems({ lat: latitude, lng: longitude });

  closeAddLocation((result as MapLocationDto).id, latitude, longitude);
  clearForm();

  sparkStore.reloadSparkableLocations();
  sparkStore.reloadGpsLocations();

  emits(GamComponentsEmits.SUCCESS);

  isLoading.value = false;
};

const handleQuickSubmit = async () => {
  if (!gamForm.value?.form || !canQuickSubmit.value || isLoading.value) return;

  const formType = gamForm.value.form.type;
  if (!formType || (Array.isArray(formType) && formType.length === 0)) {
    (gamForm.value.form as any).type = [LocationType.CHESS_FRIENDLY];
  }

  isLoading.value = true;

  const result = await handleCreateLocation(gamForm.value.form);

  if (!result) {
    errorMessage.value = {
      label: 'Something went wrong.',
      variant: GamButtonMessageVariant.ERROR,
    };
    isLoading.value = false;
    return;
  }

  if ((result as GamButtonMessageType).variant === GamButtonMessageVariant.ERROR) {
    errorMessage.value = result as GamButtonMessageType;
    isLoading.value = false;
    return;
  }

  const { latitude, longitude } = mapPinTableStore.getLocationParam() || {};
  latitude && longitude && mapPinTableStore.addUniqueItems({ lat: latitude, lng: longitude });

  closeAddLocation((result as MapLocationDto).id, latitude, longitude);
  clearForm();

  sparkStore.reloadSparkableLocations();
  sparkStore.reloadGpsLocations();

  emits(GamComponentsEmits.SUCCESS);

  isLoading.value = false;
};

const getAddLocSidePanel = computed((): GamSidePanelType => {
  return {
    isOpen: route.query.side_panel === 'add_location' && route.query.mobile_step !== 'pick_coordinate',
    isComplementary: true,
    isModal: false,
    id: panelId,
    direction: GamCollapseDirection.LEFT,
    option: GamSidePanelOption.ADD,
    preventOpenState: true,
    bodyClass: 'no-header',
    onExpand: () => {
      // Respond to location expansion.
    },
  };
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
#location-form {
  .panel-body {
    width: var(--side-panel-form-width);
  }

  .panel-body.is-mobile {
    width: 100svw;
  }
}

:deep(.panel-body.no-header) {
  padding-top: var(--spacing-xl) !important;
}

.add-location-drawer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 802;
  pointer-events: none;
}

.drawer-card {
  pointer-events: auto;
  border-radius: 1.5rem 1.5rem 0 0;
  border: 1px solid var(--color-white-10);
  border-bottom: 0;
  background: var(--color-dark-700);
  display: flex;
  flex-direction: column;
  z-index: 803;
}

.drawer-card-title {
  padding: 1rem 1.5rem 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-white);
  text-align: center;
}

.drawer-card-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  padding: 1.5rem 1.5rem 1rem;
}

.drawer-card-ctas {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0.75rem 1.5rem var(--safe-bottom-offset);
}

.add-location-bottom-ctas {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1000;

  &.is-desktop {
    left: calc(417px / 2);
  }
}

.cta-circle {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;

  :deep(.icon-arrow-right),
  :deep(.icon-arrow-left) {
    transform: scale(0.85);
  }

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  &.is-active {
    background-color: var(--color-pink);
    color: var(--color-white);

    @include hover {
      filter: brightness(1.1);
    }
  }

  &.cta-check.is-active {
    background-color: var(--color-pink);
    color: var(--color-white);

    @include hover {
      filter: brightness(1.1);
    }
  }

  @include press;
}

.cta-spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid var(--color-white-30);
  border-top-color: var(--color-white);
  border-radius: 50%;
  animation: spin 600ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 200ms ease;
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;
}
</style>
